import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from "@angular/common/http";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { AmplifyUIAngularModule } from "@aws-amplify/ui-angular";
import { TreeviewModule } from "@ingenmaffen/ngx-treeview";
import { NbEvaIconsModule } from "@nebular/eva-icons";
import { NbRoleProvider, NbSecurityModule } from "@nebular/security";
import {
  NbAccordionModule,
  NbActionsModule,
  NbButtonModule,
  NbCardModule,
  NbCheckboxModule,
  NbContextMenuModule,
  NbDatepickerModule,
  NbDialogModule,
  NbFormFieldModule,
  NbIconModule,
  NbInputModule,
  NbLayoutModule,
  NbListModule,
  NbMenuModule,
  NbPopoverModule,
  NbSelectModule,
  NbSidebarModule,
  NbStepperModule,
  NbTabsetModule,
  NbThemeModule,
  NbTimepickerModule,
  NbToastrModule,
  NbToggleModule,
  NbUserModule,
} from "@nebular/theme";
import { EffectsModule } from "@ngrx/effects";
import { StoreRouterConnectingModule } from "@ngrx/router-store";
import { StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { NgxChartsModule } from "@swimlane/ngx-charts";
import { NgxDatatableModule } from "@swimlane/ngx-datatable";
import { NgJsonEditorModule } from "ang-jsoneditor";
import Amplify from "aws-amplify";
import { ClipboardModule } from "ngx-clipboard";
import { NgxDropzoneModule } from "ngx-dropzone";
import { MarkdownModule } from "ngx-markdown";
import { environment } from "../environments/environment";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { AppEffects } from "./app.effects";
import { reducers } from "./app.reducer";
import { BoxArchiveComponent } from "./box-archive/box-archive.component";
import { BoxSubscribersComponent } from "./box-subscribers/box-subscribers.component";
// Components
import { AppDetailComponent } from "./components/app-detail/app-detail.component";
import { AppInfoComponent } from "./components/app-info/app-info.component";
import { AppOverviewComponent } from "./components/app-overview/app-overview.component";
import { DashboardLayoutComponent } from "./components/dashboard-layout/dashboard-layout.component";
import { HeaderComponent } from "./components/header/header.component";
import { LandingpageLayoutComponent } from "./components/landingpage-layout/landingpage-layout.component";
import { ParentTagSelectComponent } from "./components/parent-tag-select/parent-tag-select.component";
import { AppColFromIdsPipe } from "./components/pipes/AppColPipe";
import { DateTimePipe } from "./components/pipes/DateTimePipe";
import {
  StatisticsPipe,
  StatisticsPipeDuration,
  StatisticsPipeDurationAvg,
  StatisticsPipeExtDuration,
} from "./components/pipes/StatisticsPipe";
import { TagColFromIdsPipe } from "./components/pipes/TagColPipe";
import { SidebarComponent } from "./components/sidebar/sidebar.component";
import { TagDetailComponent } from "./components/tag-detail/tag-detail.component";
import { TagInfoComponent } from "./components/tag-info/tag-info.component";
import { TagSelectComponent } from "./components/tag-select/tag-select.component";
import { TagsOverviewComponent } from "./components/tags-overview/tags-overview.component";
import { TestPushComponent } from "./components/test-push/test-push.component";
// Directive
import { PasswordValidatorDirective } from "./password-validator.directive";
// Services
import { AuthGuardService } from "./services/auth-guard/auth-guard.service";
import { RoleProviderService } from "./services/role-provider/role.provider.service";
import { TokenInterceptor } from "./token.interceptor";
import { HomeComponent } from "./views/home/home.component";
// Views
import { AnalyticsComponent } from "./views/analytics/analytics.component";
import { ImprintComponent } from "./views/imprint/imprint.component";
import { LoginComponent } from "./views/login/login.component";
import { NotfoundComponent } from "./views/notfound/notfound.component";
import { PrivacyComponent } from "./views/privacy/privacy.component";
import { SupportComponent } from "./views/support/support.component";

Amplify.configure({
  aws_project_region: environment.region,
  aws_cognito_identity_pool_id: environment.identityPoolId,
  aws_cognito_region: environment.region,
  aws_user_pools_id: environment.userPoolId,
  aws_user_pools_web_client_id: environment.clientId,
  oauth: {},
});
@NgModule({
  declarations: [
    AppComponent,
    AppDetailComponent,
    AppOverviewComponent,
    DashboardLayoutComponent,
    DateTimePipe,
    TagColFromIdsPipe,
    AppColFromIdsPipe,
    StatisticsPipe,
    StatisticsPipeDuration,
    StatisticsPipeDurationAvg,
    StatisticsPipeExtDuration,
    HeaderComponent,
    LoginComponent,
    NotfoundComponent,
    SidebarComponent,
    TagsOverviewComponent,
    TagDetailComponent,
    ParentTagSelectComponent,
    TagSelectComponent,
    TestPushComponent,
    ImprintComponent,
    PrivacyComponent,
    LandingpageLayoutComponent,
    AppInfoComponent,
    PasswordValidatorDirective,
    TagInfoComponent,
    HomeComponent,
    BoxArchiveComponent,
    BoxSubscribersComponent,
    AnalyticsComponent,
    SupportComponent,
  ],
  imports: [
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    ClipboardModule,
    EffectsModule.forRoot([AppEffects]),
    StoreModule.forRoot(reducers),
    StoreDevtoolsModule.instrument(),
    FormsModule,
    HttpClientModule,
    NbStepperModule,
    AmplifyUIAngularModule,
    NbButtonModule,
    NbDialogModule.forRoot(),
    NbThemeModule.forRoot({ name: "default" }),
    NbLayoutModule,
    NbToggleModule,
    NbEvaIconsModule,
    NbSidebarModule.forRoot(),
    NbSelectModule,
    NbMenuModule.forRoot(),
    NbCardModule,
    NbDatepickerModule.forRoot(),
    NbFormFieldModule,
    NbTimepickerModule.forRoot(),
    NbIconModule,
    NbInputModule,
    NbListModule,
    NbPopoverModule,
    NbUserModule,
    NbContextMenuModule,
    NgxChartsModule,
    NgxDatatableModule,
    NgJsonEditorModule,
    NgxDropzoneModule,
    MarkdownModule.forRoot(),
    NbSecurityModule.forRoot({
      accessControl: {
        user: {
          view: ["stepper"],
        },
        admin: {
          parent: "user",
          view: ["clients"],
        },
      },
    }),
    NbToastrModule.forRoot({
      icon: { icon: "" },
      toastClass: "nb-toast-custom",
    }),
    ReactiveFormsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpTranslateLoader,
        deps: [HttpClient],
      },
    }),
    NbActionsModule,
    NbTabsetModule,
    NbCheckboxModule,
    NbAccordionModule,
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
    }),
    StoreRouterConnectingModule.forRoot(),
    TreeviewModule.forRoot(),
  ],
  providers: [
    AuthGuardService,
    [{ provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true }],
    DateTimePipe,
    TagColFromIdsPipe,
    AppColFromIdsPipe,
    StatisticsPipe,
    StatisticsPipeDuration,
    StatisticsPipeDurationAvg,
    StatisticsPipeExtDuration,
    RoleProviderService,
    { provide: NbRoleProvider, useClass: RoleProviderService },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

// AOT compilation support
export function httpTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
